import cx from 'classnames';

import {Carousel, Icon, Text} from '#component/base';
import {useViewportSize} from '#hook';
import {sprinkles} from '#style/sprinkles';

import {ReferenceSectionProps} from './ReferenceSection.types';

import * as css from './ReferenceSection.css';

/**
 * This component renders a content section that includes a list of references.
 * On mobile devices, this is rendered as a carousel. This is used primarily by
 * the "Info" pages, including "Understanding your results" and "Surprised
 * by your results?".
 */
export const ReferenceSection = ({
  className,
  references = [],
  ...otherProps
}: ReferenceSectionProps) => {
  const {isMobile} = useViewportSize();

  const renderedReferences = references.map(({authors, title, url}, i) => (
    <a
      className={cx(
        css.link,
        sprinkles({
          pT: [0, i === 0 ? 0 : 48],
          shadow: isMobile || i === references.length - 1 ? 'none' : 'underline',
        }),
      )}
      key={i}
      href={url}
      rel="noreferrer"
      target="_blank">
      <Text color="deepBurgundy" variant="label">
        {authors}
      </Text>
      <span
        className={sprinkles({
          content: 'between',
          dir: ['col', 'row'],
          flex: 'flex',
          gap: [16, 32],
          items: 'start',
        })}>
        <Text color="doveGray" variant="heading3">
          {title}
        </Text>

        <span className={sprinkles({content: 'between', flex: 'flex', gap: 16, items: 'center'})}>
          <Text className={css.linkText} color="orange" variant="buttonSmall">
            See reference
          </Text>

          <Icon
            aria-label="View resource"
            className={sprinkles({shrink: 0})}
            color="orange"
            name="ArrowUpRight"
            size={isMobile ? 24 : 'large'}
            weight="light"
          />
        </span>
      </span>
    </a>
  ));

  return (
    <div className={cx(sprinkles({gap: [64, 72, 16], layout: 'grid'}), className)} {...otherProps}>
      <h2
        className={sprinkles({
          colSpan: [4, 6, 5],
          colStart: [1, 2, 1],
          dir: 'col',
          flex: 'flex',
          gap: [8, 24],
        })}>
        <Text variant="label" color="orange">
          References
        </Text>

        <Text variant="heading2" color="deepBurgundy">
          Learn more
        </Text>
      </h2>

      <Carousel className={css.carousel} paddings={[24]} widths={['100vw']}>
        {renderedReferences}
      </Carousel>

      <div
        className={sprinkles({
          colSpan: [4, 6, 7],
          colStart: [1, 2, 6],
          dir: 'col',
          flex: ['none', 'flex'],
        })}>
        {renderedReferences}
      </div>
    </div>
  );
};
